<template>
  <div class="history-wrapper">
    <header class="history-header">
      <div class="history-title">
        <p class="eyebrow">Registro de accesos</p>
        <h2>Entradas escaneadas</h2>
        <p v-if="lastScan" class="last-scan">Último escaneo: <strong>{{ lastScan }}</strong></p>
      </div>
      <div class="history-summary">
        <article>
          <span>Total escaneadas</span>
          <strong>{{ filteredReservations.length }}</strong>
        </article>
        <article>
          <span>Personas verificadas</span>
          <strong>{{ totalPeople }}</strong>
        </article>
      </div>
      <div class="history-controls">
        <label>
          <span>Filtrar por fecha de escaneo</span>
          <input type="date" v-model="selectedDate" />
        </label>
        <button v-if="selectedDate" class="link-btn" type="button" @click="clearDateFilter">Ver todo</button>
      </div>
    </header>

    <section v-if="loading" class="history-empty">
      <p>Cargando historial…</p>
    </section>
    <section v-else-if="error" class="history-empty">
      <p>{{ error }}</p>
    </section>
    <section v-else-if="filteredReservations.length" class="history-grid">
      <article
        v-for="reservation in filteredReservations"
        :key="reservation.id"
        class="history-card"
      >
        <div class="history-card-header">
          <div>
            <h3>{{ reservation.name }}</h3>
            <p class="history-contact">{{ reservation.email || reservation.phone || 'Sin contacto' }}</p>
          </div>
          <span class="status-pill">{{ reservation.status }}</span>
        </div>
        <div class="history-tags">
          <span>{{ reservation.recordType === 'walkin' ? 'Walk-in' : 'Reservación' }}</span>
          <span>{{ formatDate(reservation.date) }} · {{ formatTime(reservation.time) }}</span>
          <span>{{ reservation.people || 0 }} {{ (reservation.people || 0) === 1 ? 'persona' : 'personas' }}</span>
          <span v-if="reservation.tableNumber">Mesa {{ reservation.tableNumber }}</span>
          <span v-if="reservation.tableGroupName">Grupo {{ reservation.tableGroupName }}</span>
        </div>
        <div class="history-comments" v-if="reservation.specialNotes">
          <span class="history-label">Comentarios del cliente</span>
          <p>{{ reservation.specialNotes }}</p>
        </div>
        <footer class="history-meta">
          <span>Escaneada: {{ formatDateTime(reservation.scannedAt) }}</span>
          <span>Ref: {{ reservation.id }}</span>
        </footer>
      </article>
    </section>
    <section v-else class="history-empty">
      <p v-if="selectedDate">No hay entradas escaneadas para esa fecha.</p>
      <p v-else>No hay entradas escaneadas aún.</p>
      <p class="hint">Escanea un QR desde el panel principal para comenzar a registrar entradas.</p>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/init';
import { parseLocalDate, formatLocalDate } from '../utils/date';
import { formatTimeLabel } from '../utils/time';

export default {
  name: 'ReservationHistoryView',
  setup() {
    const scannedReservations = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const selectedDate = ref(formatLocalDate(new Date()));

    const formatTime = (time) => formatTimeLabel(time) || '--:--';

    const formatDate = (dateString) => {
      if (!dateString) return 'Sin fecha';
      const date = parseLocalDate(dateString);
      if (!date) return dateString;
      return date.toLocaleDateString('es-PR', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const formatDateTime = (iso) => {
      if (!iso) return 'Sin registro';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      return date.toLocaleString('es-PR', { dateStyle: 'medium', timeStyle: 'short' });
    };

    const getScanDate = (reservation) => {
      const iso = reservation?.scannedAt || reservation?.updatedAt || reservation?.createdAt;
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      return formatLocalDate(date);
    };

    const filteredReservations = computed(() => {
      if (!selectedDate.value) return scannedReservations.value;
      return scannedReservations.value.filter((reservation) => (
        getScanDate(reservation) === selectedDate.value
      ));
    });

    const totalPeople = computed(() => filteredReservations.value.reduce((sum, item) => sum + (Number(item.people) || 0), 0));

    const lastScan = computed(() => {
      if (!filteredReservations.value.length) return '';
      const first = filteredReservations.value[0];
      return formatDateTime(first.scannedAt || first.updatedAt || first.createdAt);
    });

    const clearDateFilter = () => {
      selectedDate.value = '';
    };

    onMounted(() => {
      try {
        const q = query(
          collection(db, 'reservationScans'),
          orderBy('scannedAt', 'desc'),
          limit(100)
        );

        onSnapshot(
          q,
          (snapshot) => {
            scannedReservations.value = snapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data()
            }));
            loading.value = false;
          },
          (err) => {
            console.error('Error en el listener de escaneos:', err);
            error.value = err.message || 'No se pudo cargar el historial de reservaciones escaneadas.';
            loading.value = false;
          }
        );
      } catch (err) {
        console.error('Error al configurar el historial de escaneos:', err);
        error.value = 'No se pudo iniciar la escucha de reservaciones escaneadas.';
        loading.value = false;
      }
    });

    return {
      scannedReservations,
      filteredReservations,
      loading,
      error,
      selectedDate,
      clearDateFilter,
      totalPeople,
      lastScan,
      formatTime,
      formatDate,
      formatDateTime
    };
  }
};
</script>

<style scoped>
.history-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.history-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.history-title {
  min-width: 220px;
}

.last-scan {
  margin: 6px 0 0;
  color: rgba(15, 23, 42, 0.7);
  font-size: 14px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.25em;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.6);
  margin: 0 0 4px;
}

.history-summary {
  display: flex;
  gap: 12px;
}

.history-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
}

.history-controls label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.6);
}

.history-controls input {
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  font-size: 13px;
}

.link-btn {
  border: none;
  background: transparent;
  color: #0f7bff;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-align: left;
}

.history-summary article {
  border-radius: 16px;
  border: 1px solid rgba(15, 23, 42, 0.1);
  padding: 10px 14px;
  min-width: 140px;
  background: #fff;
}

.history-summary span {
  font-size: 12px;
  color: rgba(15, 23, 42, 0.6);
}

.history-summary strong {
  display: block;
  font-size: 22px;
  color: #0f172a;
}

.history-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 14px;
  max-height: 60vh;
  overflow: auto;
}

.history-card {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 18px;
  padding: 14px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.history-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.history-contact {
  margin: 2px 0 0;
  color: rgba(15, 23, 42, 0.7);
}

.status-pill {
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  background: rgba(15, 23, 42, 0.08);
  text-transform: uppercase;
}

.history-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.8);
}

.history-comments {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.9);
  line-height: 1.5;
}

.history-label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: rgba(15, 23, 42, 0.55);
  margin-bottom: 6px;
}

.history-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.6);
}

.history-empty {
  text-align: center;
  padding: 40px 0;
  color: rgba(15, 23, 42, 0.6);
}

.hint {
  margin-top: 6px;
  font-size: 13px;
}
</style>
